import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, FormInput, FormInputTypes, IWarning } from '@praktio/common-ui';
import { Helmet } from 'react-helmet';

import { resetPassword } from '../../ducks/users';
import {
  ErrorCodes,
  InvalidPasswordFormWarning,
  NetworkErrorFormWarning,
  ResetPasswordPageFields,
  SamePasswordFormWarning,
  SiteKey,
  SSOPasswordNotice,
} from '../../utils/constants';
import * as api from '../../utils/api';

import UnauthenticatedWrapper from '../common/UnauthenticatedWrapper/UnauthenticatedWrapper';
import ShowHidePasswordButton from '../common/ShowHidePasswordButton/ShowHidePasswordButton';

import './ResetPassword.scss';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const ResetPassword = () => {
  const query = useQuery();
  const resetToken = query.get('resetToken');
  const userId = query.get('id');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [token, setToken] = useState<string | undefined>();
  const [passwordFieldType, setPasswordFieldType] = useState<FormInputTypes>('password');

  const [newPasswordError, setNewPasswordError] = useState<string | undefined>();
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState<string | undefined>();
  const [captchaError, setCaptchaError] = useState<string | undefined>();
  const [isSSOUser, setIsSSOUser] = useState(false);
  const [warning, setWarning] = useState<IWarning | undefined>();

  useEffect(() => {
    const getIsSSOUser = async () => {
      if (userId) {
        const response = await api.getIsSSOUser(userId);
        setIsSSOUser(response.data.isSSOUser);
      }
    };

    if (userId) {
      getIsSSOUser();
    }
  }, [userId]);

  interface ResetPasswordFormIProps {
    field?: string;
    value?: string;
  }

  const onFormChange = ({ field, value }: ResetPasswordFormIProps) => {
    if (field) {
      if (field === ResetPasswordPageFields.newPassword) {
        setNewPasswordError(undefined);
        setNewPassword(value || '');
      }
      if (field === ResetPasswordPageFields.confirmNewPassword) {
        setConfirmNewPasswordError(undefined);
        setConfirmNewPassword(value || '');
      }
      if (field === ResetPasswordPageFields.captcha) {
        setCaptchaError(undefined);
        setToken(value || undefined);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    let currentNewPasswordError = newPasswordError;
    let currentConfirmNewPasswordError = confirmNewPasswordError;
    let currentCaptchaError = captchaError;

    // Set error if new password is empty.
    if (!newPassword || newPassword === '') {
      currentNewPasswordError = 'This field cannot be blank.';
      setNewPasswordError(currentNewPasswordError);
    }

    // Set error if confirm new password is emtpy.
    if (!confirmNewPassword || confirmNewPassword === '') {
      currentConfirmNewPasswordError = 'This field cannot be blank.';
      setConfirmNewPasswordError(currentConfirmNewPasswordError);
    }

    // Set error if passwords do not match.
    if (newPassword !== confirmNewPassword) {
      currentConfirmNewPasswordError = 'Passwords do not match.';
      setConfirmNewPasswordError(currentConfirmNewPasswordError);
    }

    // Set error if recaptcha is not completed.
    if (!token || token === '') {
      currentCaptchaError = 'Please complete the ReCAPTCHA.';
      setCaptchaError(currentCaptchaError);
    }

    // Change password if:
    // - resetToken is present
    // - userId is present
    // - new passwords are present
    // - captcha token is present
    // - there are no errors.
    if (
      newPassword &&
      confirmNewPassword &&
      token &&
      userId &&
      resetToken &&
      !currentNewPasswordError &&
      !currentConfirmNewPasswordError &&
      !currentCaptchaError
    ) {
      try {
        await dispatch(
          resetPassword({ userId, password: newPassword, resetToken, token }, navigate),
        );
      } catch (e) {
        const error = e as IError;
        const { errorCode } = error;
        let newWarning = NetworkErrorFormWarning;
        switch (errorCode) {
          case ErrorCodes.InvalidPassword:
            newWarning = InvalidPasswordFormWarning;
            break;
          case ErrorCodes.SamePassword:
            newWarning = SamePasswordFormWarning;
            break;
          default:
            break;
        }

        setWarning(newWarning);
      }

      recaptchaRef.current?.reset();
    }
  };

  const togglePassword = () => {
    setPasswordFieldType(passwordFieldType === 'password' ? 'text' : 'password');
  };

  return (
    <UnauthenticatedWrapper formName="Reset Password">
      <Helmet>
        <title>Reset Password - Praktio</title>
      </Helmet>
      <div className="ResetPassword">
        {isSSOUser && <div className="sso-description">{SSOPasswordNotice}</div>}
        {warning && (
          <div className="alert alert-danger" role="alert">
            <div className="alert-title">{warning.title}</div>
            <div className="alert-body">{warning.body}</div>
          </div>
        )}
        {captchaError && (
          <div className="alert alert-danger" role="alert">
            <div className="alert-title">Form Error</div>
            <div className="alert-body">{captchaError}</div>
          </div>
        )}
        <Form
          className="ResetPasswordForm"
          id="reset-password-form"
          onSubmit={(e: any) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FormInput
            label="New Password"
            placeholder="New Password"
            fieldName="newPassword"
            type={passwordFieldType}
            value={newPassword}
            onChange={(e: any) =>
              onFormChange({
                field: ResetPasswordPageFields.newPassword,
                value: e.target.value,
              })
            }
            error={newPasswordError}
            required
            labelWrapperClassName="label-show-hide-group d-flex justify-content-between"
            labelAdditions={
              <ShowHidePasswordButton
                clickFunction={togglePassword}
                passwordVisible={passwordFieldType === 'text'}
                passwordFieldType={passwordFieldType}
              />
            }
            noTrimming
          />
          <FormInput
            label="Confirm New Password"
            placeholder="Confirm New Password"
            fieldName="confirmNewPassword"
            type={passwordFieldType}
            value={confirmNewPassword}
            onChange={(e: any) =>
              onFormChange({
                field: ResetPasswordPageFields.confirmNewPassword,
                value: e.target.value,
              })
            }
            error={confirmNewPasswordError}
            required
          />
          <div className="ReCAPTCHA-container mb-3">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={SiteKey}
              onChange={(token) =>
                onFormChange({
                  field: ResetPasswordPageFields.captcha,
                  value: token || undefined,
                })
              }
            />
          </div>
          <Button
            onClick={(e: any) => handleSubmit(e)}
            form="reset-password-form"
            type="submit"
            btnStyle="primary"
          >
            RESET PASSWORD
          </Button>
        </Form>
      </div>
    </UnauthenticatedWrapper>
  );
};

export default ResetPassword;
